// @flow

import React, {useState, useRef, useEffect, useCallback} from 'react';
import {Grid} from '@material-ui/core';
import {List} from 'immutable';
import CheckoutInputCard from '../CheckoutInput/CheckoutInputCard';

import {actions as CheckoutActions} from '../../duck';
import {useBindedActions} from '../../../../../utils/globalStore';

import type {CheckoutInput} from '../../../../../entities';
import {useTypedSelector} from '../../../../../store';
export type Movement = {
  init?: {pos: number, id: string},
  end?: {pos: number, id: string},
};

const actions = {
  sortCheckoutInput: CheckoutActions.sortCheckoutInput,
};

type Props = {
  inputs: CheckoutInput[],
};
export default function DraggableInputs(props: Props) {
  const {inputs} = props;

  const [localInputs, setLocalInputs] = useState<CheckoutInput[]>([]);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [itemDragged, setItemDragged] = useState(undefined);
  const [movement, setMovement] = useState<Movement | null>(null);

  const isSaving = useTypedSelector(state => state.salesProcessConfig.isSaving);
  const {sortCheckoutInput} = useBindedActions(actions);

  const itemDraggedRef = useRef();

  useEffect(() => {
    if (inputs?.length) {
      setLocalInputs(inputs);
    }
  }, [inputs]);

  const getIndexOfElement = (arr = [], item: {id: string}) =>
    arr.findIndex(el => el.id === item.id);

  const handleDragStart = (e, item) => {
    const dragImg = document.createElement('img');
    dragImg.src =
      'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    e.dataTransfer.setDragImage(dragImg, 0, 0);
    setIsDragging(true);
    setItemDragged(item);
    setMovement({
      init: {
        pos: getIndexOfElement(localInputs, item),
        id: item.id,
      },
      end: undefined,
    });
    itemDraggedRef.current = e.target;
  };

  const handleDragEnter = (e, item) => {
    if (itemDraggedRef.current !== e.target) {
      if (itemDragged) {
        const pos = getIndexOfElement(localInputs, item);
        setMovement({
          ...movement,
          end: {
            pos: pos,
            id: item.id,
          },
        });

        setLocalInputs(prev => {
          const indexElementSort = prev
            ? prev.findIndex(el => el.id === itemDragged.id)
            : -1;
          const sList = List(prev)
            .delete(indexElementSort)
            .insert(pos, itemDragged);
          const s = sList.toArray();
          return s;
        });
      }
    }
  };

  const onDragEnd = useCallback(() => {
    if (itemDragged && itemDraggedRef.current && movement && localInputs) {
      setIsDragging(false);
      setItemDragged(null);

      const {init, end} = movement;
      if (init && end && init.pos !== end.pos) {
        sortCheckoutInput({
          sortRequest: movement,
        });
        setMovement(null);
      }
      //$FlowFixMe
      itemDraggedRef.current.removeEventListener('dragend', onDragEnd);
      itemDraggedRef.current = null;
    }
  }, [itemDragged, movement, localInputs, sortCheckoutInput]);

  useEffect(() => {
    if (isDragging && itemDraggedRef && itemDraggedRef.current) {
      itemDraggedRef.current.addEventListener('dragend', onDragEnd);

      return () => {
        itemDraggedRef &&
          itemDraggedRef.current &&
          itemDraggedRef.current.removeEventListener('dragend', onDragEnd);
      };
    }
  }, [isDragging, onDragEnd]);

  return (
    <Grid container spacing={3}>
      {localInputs.map((el, index) => (
        <Grid item xs={12}>
          <CheckoutInputCard
            item={el}
            index={index}
            key={el.id}
            handleDragStart={handleDragStart}
            handleDragEnter={handleDragEnter}
            isDragging={isDragging}
            disabled={isSaving}
            dragItemId={itemDragged ? itemDragged.id : '-1'}
          />
        </Grid>
      ))}
    </Grid>
  );
}
